import ky, { HTTPError, type KyInstance, type Options } from 'ky';

/**
 * Central HTTP client.
 * - Sends session cookie via `credentials: 'include'`
 * - On 401, dispatches a global `auth:unauthorized` event
 *   (AuthProvider listens and clears user state + redirects to /login)
 * - Normalizes API errors into ApiError
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly field: string | undefined;
  readonly payload: unknown;

  constructor(
    message: string,
    status: number,
    options: { code?: string; field?: string; payload?: unknown } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options.code;
    this.field = options.field;
    this.payload = options.payload;
  }
}

const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

export function dispatchUnauthorized(): void {
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
}

export function onUnauthorized(handler: () => void): () => void {
  window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
  return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handler);
}

// In prod the SPA can live on a different subdomain than the API
// (e.g. postchecker.titanteam.org vs postchecker-backend.titanteam.org), so
// the base URL is picked at build time via VITE_API_BASE_URL. When unset
// (dev / single-origin prod), the relative `/api` path is used and Vite's
// proxy forwards to the backend.
const API_ORIGIN = import.meta.env.VITE_API_BASE_URL
  ? String(import.meta.env.VITE_API_BASE_URL).replace(/\/+$/, '')
  : '';
const API_PREFIX_URL = `${API_ORIGIN}/api`;

/**
 * Build an absolute /api/* URL, honouring VITE_API_BASE_URL when the SPA and
 * API are deployed on different origins. Use this for download links and any
 * native `fetch` calls that bypass the `http` client.
 */
export function apiUrl(pathFromApi: string): string {
  const clean = pathFromApi.startsWith('/') ? pathFromApi : `/${pathFromApi}`;
  return `${API_PREFIX_URL}${clean.replace(/^\/api/, '')}`;
}

export const http: KyInstance = ky.create({
  prefixUrl: API_PREFIX_URL,
  credentials: 'include',
  timeout: 30_000,
  retry: {
    limit: 1,
    methods: ['get'],
    statusCodes: [502, 503, 504],
  },
  hooks: {
    beforeError: [
      async (error: HTTPError) => {
        const { response } = error;
        if (response?.status === 401) {
          dispatchUnauthorized();
        }
        try {
          const data = (await response.clone().json()) as {
            detail?: string;
            code?: string;
            field?: string;
          };
          const apiError = new ApiError(data.detail ?? response.statusText, response.status, {
            code: data.code,
            field: data.field,
            payload: data,
          });
          (error as unknown as { apiError: ApiError }).apiError = apiError;
          error.message = apiError.message;
        } catch {
          // fall through with original error
        }
        return error;
      },
    ],
  },
});

/**
 * Convenience wrappers that return typed JSON directly and surface ApiError.
 */
export async function apiGet<T>(path: string, options?: Options): Promise<T> {
  return runRequest<T>(() => http.get(path, options).json<T>());
}

export async function apiPost<T>(path: string, body?: unknown, options?: Options): Promise<T> {
  return runRequest<T>(() => http.post(path, { json: body, ...options }).json<T>());
}

export async function apiPut<T>(path: string, body?: unknown, options?: Options): Promise<T> {
  return runRequest<T>(() => http.put(path, { json: body, ...options }).json<T>());
}

export async function apiPatch<T>(path: string, body?: unknown, options?: Options): Promise<T> {
  return runRequest<T>(() => http.patch(path, { json: body, ...options }).json<T>());
}

export async function apiDelete<T>(path: string, options?: Options): Promise<T> {
  return runRequest<T>(() => http.delete(path, options).json<T>());
}

async function runRequest<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof HTTPError) {
      const apiError = (err as unknown as { apiError?: ApiError }).apiError;
      throw apiError ?? new ApiError(err.message, err.response.status);
    }
    throw err;
  }
}
